'use client';
/**
 * inflow — 내부 유입 계측(internal_nav). 기존 수집 경로(lib/analytics track → /api/analytics/events → user_events) 그대로.
 *   event_type 'click' · event_name 'internal_nav' · properties { from(출발 지면), to(도착 유형 site|blog), href }.
 * 감싼 영역 안에서 [data-inflow-to] 안의 <a> 를 누르면 한 번 보낸다. 렌더 HTML 은 서버와 같다(클로킹 없음 · 클릭 리스너만).
 * also: 이 컴포넌트 밖의 기존 링크(예: 블로그 「이 글의 현장」 카드)를 같은 이벤트로 세고 싶을 때의 선택자 — 문서 위임 리스너 1개.
 */
import { useEffect, type ReactNode } from 'react';
import { track } from '@/lib/analytics';

export type InflowFrom = 'apt_detail' | 'blog_end' | 'area_hub';
export type InflowTo = 'site' | 'blog';

function send(from: InflowFrom, to: string, href: string | null) {
  try { track('click', 'internal_nav', { from, to, href }); } catch { /* 계측 실패는 조용히 */ }
}

export function inflowTargetOf(target: EventTarget | null): { to: string; href: string | null } | null {
  const el = target as Element | null;
  if (!el || typeof (el as any).closest !== 'function') return null;
  const a = el.closest('a');
  if (!a) return null;
  const box = a.closest('[data-inflow-to]');
  if (!box) return null;
  return { to: String(box.getAttribute('data-inflow-to') ?? ''), href: a.getAttribute('href') };
}

export default function InflowTrack({ from, children, also }: { from: InflowFrom; children: ReactNode; also?: { selector: string; to: InflowTo } }) {
  useEffect(() => {
    if (!also) return;
    const onClick = (e: MouseEvent) => {
      const el = e.target as Element | null;
      const a = el && typeof el.closest === 'function' ? el.closest(also.selector) : null;
      if (a) send(from, also.to, a.getAttribute('href'));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [from, also]);
  return (
    <div
      data-inflow={from}
      onClickCapture={(e) => { const t = inflowTargetOf(e.target); if (t) send(from, t.to, t.href); }}
    >
      {children}
    </div>
  );
}
